/**
 * Виды цен кабинета AQSI. В кабинете нет справочника «виды цен» (проверено по описанию API `api.aqsi.ru/pub/v1/swagger.json`,
 * 18.10.2026): у товара фиксированный набор цен, и новые завести нельзя. Список ниже — этот набор, с пояснением, что каждая цена
 * значит и как с ней работает наша служба. Данных с сервера здесь нет: набор определяет API кабинета, а не база.
 */
export type PriceKindAccess = 'read-write' | 'write-only' | 'cabinet-only' | 'read-only'

export interface PriceKind {
  id: string
  name: string
  /** Что это за цена — простыми словами. */
  meaning: string
  /** Поле API кабинета — для тех, кто сверяет с документацией AQSI. */
  apiField: string
  access: PriceKindAccess
}

export const PRICE_KINDS: readonly PriceKind[] = [
  {
    id: 'price',
    name: 'Цена товара',
    meaning: 'Основная цена карточки товара: по ней товар продаётся на кассе, если для магазина не задана своя цена.',
    apiField: 'price / defaultPrice',
    access: 'read-write',
  },
  {
    id: 'shop-price',
    name: 'Цена в магазине',
    meaning: 'Цена этого товара в конкретном магазине кабинета. Задаётся отдельно для каждого магазина, к которому привязан товар.',
    apiField: 'shops[].price',
    access: 'write-only',
  },
  {
    id: 'min-price',
    name: 'Минимальная цена',
    meaning: 'Нижняя граница цены: ниже неё кассир товар не продаст.',
    apiField: 'minPrice',
    access: 'cabinet-only',
  },
  {
    id: 'last-purchase',
    name: 'Последняя закупочная',
    meaning: 'Цена последней закупки товара. Появляется сама, когда товар принимают документом приёмки, вручную её не вводят.',
    apiField: 'lastPurchasePrice',
    access: 'read-only',
  },
]

export const ACCESS_WORDS: Readonly<Record<PriceKindAccess, string>> = {
  'read-write': 'Читается и записывается из нашей системы',
  'write-only': 'Только записывается: кабинет её обратно не показывает',
  'cabinet-only': 'Только в самом кабинете AQSI',
  'read-only': 'Только чтение: заполняется приёмкой',
}

export const PRICE_KINDS_HELP = {
  fixed:
    'В кабинете AQSI нет справочника видов цен, как в 1С: у каждого товара один и тот же набор цен, и API кабинета не позволяет завести новую. Поэтому «Создать вид цен» и «Удалить» здесь недоступны, а список ниже — весь набор, который кабинет знает.',
  access:
    'Что наша система умеет делать с этой ценой через API кабинета. Часть цен кабинет принимает, но не отдаёт обратно, часть задают только в самом кабинете.',
  field: 'Имя поля в описании API AQSI (api.aqsi.ru/pub/v1/swagger.json) — чтобы сверить со справкой кабинета.',
} as const

export const CREATE_DISABLED_REASON =
  'Кабинет AQSI не умеет заводить новые виды цен: набор цен у товара фиксированный. Создавать виды цен можно в 1С.'
